<template>
  <component
    :is="isMobile ? CoreBottomSheet : CoreDialog"
    v-model="isOpened"
    data-qa="doctor-documents-modal"
    v-bind="isMobile
      ? {}
      : {
        title,
        divider: true,
        size: 'md',
      }"
  >
    <template
      v-if="!isMobile"
      #prependBtn="{ btnProps }"
    >
      <v-btn
        v-bind="btnProps"
        @click="isOpened = false"
      >
        <span class="ui-icon-close-not-a-circle" />
      </v-btn>
    </template>

    <div
      v-if="isMobile"
      class="d-flex align-center mb-2"
    >
      <v-icon
        v-if="!documents.fakeDocuments"
        color="success"
        size="24"
      >
        ui-icon-shield-checkmark-40
      </v-icon>
      <div
        class="text-h6"
        :class="documents.fakeDocuments ? 'text--text' : 'text-success--text ml-4'"
      >
        {{ title }}
      </div>
    </div>

    <div class="text-body-1 text-secondary--text mb-2">
      <template v-if="documents.fakeDocuments">
        {{ fakeDocumentsLead }}
      </template>
      <template v-else>
        {{ t('Специалист предоставил документы, а мы проверили их достоверность', l10nOptions) }}
      </template>
    </div>

    <template v-if="documents.fakeDocuments">
      <div
        v-for="(fakeDocument, index) in documents.fakeDocuments"
        :key="index"
        class="d-flex py-3"
      >
        <v-icon
          color="text-error"
          size="24"
        >
          ui-icon-circle-warning
        </v-icon>
        <div class="text-body-1 ml-4">
          {{ t(fakeDocument, l10nOptions) || fakeDocument }}
        </div>
      </div>
    </template>
    <template v-else>
      <div
        v-if="documents.isEducationConfirmed"
        class="d-flex py-3"
      >
        <v-icon
          color="text-success"
          size="24"
        >
          ui-icon-check-circle
        </v-icon>
        <div class="text-body-1 ml-4">
          {{ t('Диплом о базовом образовании', l10nOptions) }}
        </div>
      </div>
      <div
        v-if="documents.isCategoryConfirmed"
        class="d-flex py-2"
      >
        <v-icon
          v-if="documents.isCategoryValid && !documents.isCategoryExpiringSoon"
          color="text-success"
          size="24"
        >
          ui-icon-check-circle
        </v-icon>
        <v-icon
          v-else
          color="text-error"
          size="24"
        >
          ui-icon-circle-warning
        </v-icon>
        <div class="ml-4">
          <div class="text-body-1">
            {{ t('Удостоверение о категории', l10nOptions) }}
          </div>
          <div
            v-if="!documents.isCategoryValid"
            class="text-body-2 text-error--text"
          >
            {{ t('Документ уже недействителен', l10nOptions) }}
          </div>
          <div
            v-else-if="documents.categoryExpiresDate"
            class="text-body-2"
            :class="documents.isCategoryExpiringSoon ? 'text-error--text' : 'text-secondary--text'"
          >
            {{ t('Действительно до', l10nOptions) }} {{ categoryExpiresDateFormatted }}
          </div>
        </div>
      </div>
      <div
        v-if="documents.isScienceConfirmed"
        class="d-flex py-3"
      >
        <v-icon
          color="text-success"
          size="24"
        >
          ui-icon-check-circle
        </v-icon>
        <div class="text-body-1 ml-4">
          {{ scienceLabel }}
        </div>
      </div>
    </template>

    <template #actions>
      <v-btn
        class="primary--text branding-btn branding-btn--background"
        color="bg-primary"
        depressed
        block
        @click="onConfirm"
      >
        {{ confirmLabel }}
      </v-btn>
    </template>
  </component>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { format } from 'date-fns'
import i18next from 'i18next'
import useIsMobile from 'src/composables/useIsMobile'
import CoreBottomSheet from 'src/core/CoreBottomSheet/CoreBottomSheet.vue'
import CoreDialog from 'src/core/CoreDialog/CoreDialog.vue'
import type { DoctorDocumentsPayload } from 'src/components/common/DoctorDocumentsModal/types'

type Emits = {
  (_event: 'input', _value: boolean): void
}

const props = defineProps<{
  value: boolean
  documents: DoctorDocumentsPayload
}>()
const emit = defineEmits<Emits>()

const { t, language } = i18next
const l10nOptions = { ns: language }

const SCIENCE_LABEL: Record<number, string> = {
  1: 'Диплом кандидата наук',
  2: 'Диплом доктора наук',
  3: 'Аттестат профессора',
}

const isMobile = useIsMobile()
const isOpened = computed({
  get: () => props.value,
  set: value => {
    emit('input', value)
  },
})
const documents = computed(() => props.documents)

const title = computed(() => (
  documents.value.fakeDocuments?.length
    ? t('Факт выдачи части документов опровергнут', l10nOptions)
    : t('Документы проверены', l10nOptions)
))
const scienceLabel = computed(() => {
  const type = documents.value.scienceDocumentType

  if (type == null || !SCIENCE_LABEL[type]) {
    return ''
  }

  return t(SCIENCE_LABEL[type], l10nOptions)
})
const categoryExpiresDateFormatted = computed(() => {
  if (!documents.value.categoryExpiresDate) {
    return ''
  }

  return format(new Date(documents.value.categoryExpiresDate), 'dd.MM.yy')
})
const confirmLabel = computed(() => (
  documents.value.linkToDocuments
    ? t('Посмотреть документы', l10nOptions)
    : t('Хорошо', l10nOptions)
))
const fakeDocumentsLead = computed(() => t(
  'Мы отправили запрос в ответственное учреждение и получили ответ, что документ не выдавался:',
  l10nOptions,
))

function onConfirm() {
  const link = documents.value.linkToDocuments

  isOpened.value = false

  if (link && process.env.IS_CLIENT) {
    window.location.assign(link)
  }
}
</script>
